// #region imports

// --- Angular
import { Injectable } from '@angular/core';

// --- RxJs
import { Observable, BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

// HS Code
import {
  AssignedEnrichedUserDetailValuesModel,
  EnrichedUserDetailsCollectionModel,
  EnrichedUserDetailValueModel,
} from '../../../models';

// #endregion

@Injectable({
  providedIn: 'root',
})
export abstract class EnrichedUserDetailsBaseService {
  protected _hasUserDetails$: BehaviorSubject<boolean | null>;
  get hasUserDetails$(): Observable<boolean | null> {
    return this._hasUserDetails$;
  }

  protected _userDetailsCollection$: BehaviorSubject<EnrichedUserDetailsCollectionModel | null>;
  get userDetailsCollection$(): Observable<EnrichedUserDetailsCollectionModel | null> {
    return this._userDetailsCollection$;
  }

  protected _loadingCollection$: BehaviorSubject<boolean>;
  get loadingCollection$() {
    return this._loadingCollection$;
  }

  protected _savingCollection$: BehaviorSubject<boolean>;
  get savingCollection$() {
    return this._savingCollection$;
  }

  protected _assignedEnrichedUserDetails$: BehaviorSubject<
    AssignedEnrichedUserDetailValuesModel[]
  >;
  get assignedEnrichedUserDetails$(): Observable<
    AssignedEnrichedUserDetailValuesModel[]
  > {
    return this._assignedEnrichedUserDetails$;
  }

  protected _userDetailsSaved$: Subject<boolean>;
  get userDetailsSaved$(): Observable<boolean> {
    return this._userDetailsSaved$;
  }

  enrichedUserDetailValues$: Observable<EnrichedUserDetailValueModel[][] | null>;

  protected _companyId: string | undefined;

  constructor() {
    this._hasUserDetails$ = new BehaviorSubject<boolean | null>(null);
    this._userDetailsCollection$ =
      new BehaviorSubject<EnrichedUserDetailsCollectionModel | null>(null);
    this._savingCollection$ = new BehaviorSubject<boolean>(false);
    this._loadingCollection$ = new BehaviorSubject<boolean>(false);
    this._assignedEnrichedUserDetails$ = new BehaviorSubject<
      AssignedEnrichedUserDetailValuesModel[]
    >([]);
    this._userDetailsSaved$ = new Subject<boolean>();

    this.enrichedUserDetailValues$ = combineLatest([
      this.userDetailsCollection$,
      this.assignedEnrichedUserDetails$,
    ]).pipe(
      map(([userDetailsCollection, assignedEnrichedUserDetails]) => {
        if (!userDetailsCollection) {
          return null;
        }
        if (!Array.isArray(userDetailsCollection.enrichedUserDetails)) {
          return null;
        }
        return userDetailsCollection.enrichedUserDetails.map((userDetails) => {
          return userDetails.values.filter((value) => {
            return (
              value.parentId === '00000000-0000-0000-0000-000000000000' ||
              (Array.isArray(assignedEnrichedUserDetails) &&
                assignedEnrichedUserDetails.some(
                  (a) => a.enrichedUserDetailValueId === value.parentId
                ))
            );
          });
        });
      })
    );
  }

  abstract areEnrichedDetailsRequired(): Observable<boolean>;
  abstract getUserDetails(companyId: string | null): void;
  abstract updateUserDetails(userDetailValues: string[], userId: string): void;

  waitForUserDetailsLoaded(): Observable<boolean> {
    return this.loadingCollection$.pipe(
      filter((loading) => loading === false),
      take(1)
    );
  }

  toggleLoading(loading = true) {
    this._loadingCollection$.next(loading);
  }

  updateAssignedUserDetails(userDetailValues: string[], userId: string) {
    const assignedUserDetails: AssignedEnrichedUserDetailValuesModel[] =
      this._getAssignedUserDetailsFromValues(userDetailValues, userId);
    this._assignedEnrichedUserDetails$.next(assignedUserDetails);
  }

  protected _getAssignedUserDetailsFromValues(
    userDetailValues: string[],
    userId: string
  ) {
    const collection = this._userDetailsCollection$.value;
    const assignedUserDetails: AssignedEnrichedUserDetailValuesModel[] = [];
    if (collection) {
      collection.enrichedUserDetails.forEach((enrichedUserDetail) => {
        for (const userDetailValue of userDetailValues) {
          const currentUserDetailValue = enrichedUserDetail.values.find(
            (v) => v.id === userDetailValue
          );
          if (currentUserDetailValue) {
            assignedUserDetails.push({
              enrichedUserDetailId: enrichedUserDetail.id,
              enrichedUserDetailValueId: userDetailValue,
              userId: userId,
            });
          }
        }
      });
    }

    return assignedUserDetails;
  }
}
